import React, { useMemo, useState } from 'react';
import { BuildingIcon, PlusIcon, Trash2Icon, TrendingUpIcon, DollarSignIcon, PieChartIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Bar } from 'recharts';
type LoanType = 'PI' | 'IO';
interface Loan {
  id: string;
  name: string;
  amount: number;
  rate: number;
  term: number;
  type: LoanType;
  securedAgainstId: string; // ID of the property this loan is secured against
}
interface Property {
  id: string;
  name: string;
  value: number;
  weeklyRent: number;
  annualExpenses: number;
  growthRate: number;
  loans: Loan[];
}
const generateId = () => Math.random().toString(36).substr(2, 9);
export function PortfolioCashflow() {
  const [properties, setProperties] = useState<Property[]>([{
    id: 'prop-1',
    name: 'Primary Residence (PPOR)',
    value: 1000000,
    weeklyRent: 0,
    annualExpenses: 5000,
    growthRate: 5,
    loans: [{
      id: 'loan-1',
      name: 'Main Mortgage',
      amount: 600000,
      rate: 5.5,
      term: 30,
      type: 'PI',
      securedAgainstId: 'prop-1'
    }]
  }, {
    id: 'prop-2',
    name: 'Investment Property 1',
    value: 600000,
    weeklyRent: 550,
    annualExpenses: 8000,
    growthRate: 6,
    loans: [{
      id: 'loan-2',
      name: 'IP1 Mortgage',
      amount: 480000,
      rate: 6.0,
      term: 30,
      type: 'IO',
      securedAgainstId: 'prop-2'
    }, {
      id: 'loan-3',
      name: 'Equity Release for Deposit',
      amount: 150000,
      rate: 6.0,
      term: 30,
      type: 'IO',
      securedAgainstId: 'prop-1' // Secured against PPOR
    }]
  }]);
  const [expandedPropId, setExpandedPropId] = useState<string | null>('prop-2');
  const [activeTab, setActiveTab] = useState<'setup' | 'projections'>('setup');
  // Handlers
  const addProperty = () => {
    if (properties.length >= 10) return;
    const newProp: Property = {
      id: generateId(),
      name: `Property ${properties.length + 1}`,
      value: 500000,
      weeklyRent: 500,
      annualExpenses: 6000,
      growthRate: 5,
      loans: []
    };
    setProperties([...properties, newProp]);
    setExpandedPropId(newProp.id);
  };
  const removeProperty = (id: string) => {
    setProperties(properties.filter(p => p.id !== id));
  };
  const updateProperty = (id: string, field: keyof Property, value: any) => {
    setProperties(properties.map(p => p.id === id ? {
      ...p,
      [field]: value
    } : p));
  };
  const addLoan = (propId: string) => {
    setProperties(properties.map(p => {
      if (p.id === propId) {
        if (p.loans.length >= 10) return p;
        return {
          ...p,
          loans: [...p.loans, {
            id: generateId(),
            name: `Loan ${p.loans.length + 1}`,
            amount: 100000,
            rate: 6.0,
            term: 30,
            type: 'IO',
            securedAgainstId: propId
          }]
        };
      }
      return p;
    }));
  };
  const removeLoan = (propId: string, loanId: string) => {
    setProperties(properties.map(p => {
      if (p.id === propId) {
        return {
          ...p,
          loans: p.loans.filter(l => l.id !== loanId)
        };
      }
      return p;
    }));
  };
  const updateLoan = (propId: string, loanId: string, field: keyof Loan, value: any) => {
    setProperties(properties.map(p => {
      if (p.id === propId) {
        return {
          ...p,
          loans: p.loans.map(l => l.id === loanId ? {
            ...l,
            [field]: value
          } : l)
        };
      }
      return p;
    }));
  };
  // Calculations
  const results = useMemo(() => {
    let totalValue = 0;
    let totalDebt = 0;
    let totalRent = 0;
    let totalExpenses = 0;
    let totalRepayments = 0;
    // Calculate current snapshot
    properties.forEach(p => {
      totalValue += p.value;
      totalRent += p.weeklyRent * 52;
      totalExpenses += p.annualExpenses;
      p.loans.forEach(l => {
        totalDebt += l.amount;
        const r = l.rate / 100 / 12;
        const n = l.term * 12;
        let annualRepayment = 0;
        if (l.amount > 0 && r > 0) {
          if (l.type === 'PI' && n > 0) {
            const monthly = l.amount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
            annualRepayment = monthly * 12;
          } else {
            annualRepayment = l.amount * (l.rate / 100);
          }
        }
        totalRepayments += annualRepayment;
      });
    });
    const totalEquity = totalValue - totalDebt;
    const netCashflow = totalRent - totalExpenses - totalRepayments;
    const portfolioLVR = totalValue > 0 ? totalDebt / totalValue * 100 : 0;
    // Generate 10-year projections
    const projections = [];
    let currentProps = properties.map(p => ({
      ...p,
      currentValue: p.value,
      currentRent: p.weeklyRent * 52,
      currentExpenses: p.annualExpenses,
      loansState: p.loans.map(l => ({
        ...l,
        currentBalance: l.amount
      }))
    }));
    for (let year = 0; year <= 10; year++) {
      let yearValue = 0;
      let yearDebt = 0;
      let yearRent = 0;
      let yearExpenses = 0;
      let yearRepayments = 0;
      currentProps.forEach(p => {
        yearValue += p.currentValue;
        yearRent += p.currentRent;
        yearExpenses += p.currentExpenses;
        p.loansState.forEach(l => {
          yearDebt += l.currentBalance;
          const r = l.rate / 100 / 12;
          const n = l.term * 12;
          let annualRepayment = 0;
          let annualInterest = 0;
          if (l.currentBalance > 0 && r > 0) {
            if (l.type === 'PI' && n > 0) {
              const monthly = l.amount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
              annualRepayment = monthly * 12;
              // Approximate principal reduction for the year
              for (let m = 0; m < 12; m++) {
                const interest = l.currentBalance * r;
                const principal = monthly - interest;
                l.currentBalance -= principal;
                annualInterest += interest;
              }
            } else {
              annualRepayment = l.currentBalance * (l.rate / 100);
              annualInterest = annualRepayment;
            }
          }
          yearRepayments += annualRepayment;
        });
        // Grow values for next year
        p.currentValue *= 1 + p.growthRate / 100;
        p.currentRent *= 1.03; // Assume 3% rental growth
        p.currentExpenses *= 1.02; // Assume 2% expense inflation
      });
      projections.push({
        year,
        value: Math.round(yearValue),
        debt: Math.round(yearDebt),
        equity: Math.round(yearValue - yearDebt),
        netCashflow: Math.round(yearRent - yearExpenses - yearRepayments),
        rent: Math.round(yearRent),
        expenses: Math.round(yearExpenses),
        repayments: Math.round(yearRepayments)
      });
    }
    return {
      totalValue,
      totalDebt,
      totalEquity,
      netCashflow,
      portfolioLVR,
      totalRent,
      totalExpenses,
      totalRepayments,
      projections
    };
  }, [properties]);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(val);
  return <div className="space-y-8" data-id="element-307">
      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-308">
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-309">
          <div className="flex items-center gap-3 mb-2" data-id="element-310">
            <BuildingIcon className="w-5 h-5 text-blue-400" data-id="element-311" />
            <p className="text-sm font-medium text-slate-400" data-id="element-312">
              Portfolio Value
            </p>
          </div>
          <p className="text-3xl font-bold text-white" data-id="element-313">
            {formatCurrency(results.totalValue)}
          </p>
        </div>
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-314">
          <div className="flex items-center gap-3 mb-2" data-id="element-315">
            <PieChartIcon className="w-5 h-5 text-purple-400" data-id="element-316" />
            <p className="text-sm font-medium text-slate-400" data-id="element-317">
              Total Debt (LVR: {results.portfolioLVR.toFixed(1)}%)
            </p>
          </div>
          <p className="text-3xl font-bold text-white" data-id="element-318">
            {formatCurrency(results.totalDebt)}
          </p>
        </div>
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-319">
          <div className="flex items-center gap-3 mb-2" data-id="element-320">
            <TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-321" />
            <p className="text-sm font-medium text-slate-400" data-id="element-322">Total Equity</p>
          </div>
          <p className="text-3xl font-bold text-white" data-id="element-323">
            {formatCurrency(results.totalEquity)}
          </p>
        </div>
        <div className={`p-6 rounded-2xl border ${results.netCashflow >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`} data-id="element-324">
          <div className="flex items-center gap-3 mb-2" data-id="element-325">
            <DollarSignIcon className={`w-5 h-5 ${results.netCashflow >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-326" />
            <p className="text-sm font-medium text-slate-400" data-id="element-327">
              Net Annual Cashflow
            </p>
          </div>
          <p className={`text-3xl font-bold ${results.netCashflow >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-328">
            {formatCurrency(results.netCashflow)}
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-4 border-b border-white/10 pb-4" data-id="element-329">
        <button onClick={() => setActiveTab('setup')} className={`px-6 py-2 rounded-lg font-medium transition-all ${activeTab === 'setup' ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`} data-id="element-330">
          Properties & Loans Setup
        </button>
        <button onClick={() => setActiveTab('projections')} className={`px-6 py-2 rounded-lg font-medium transition-all ${activeTab === 'projections' ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`} data-id="element-331">
          Cashflow Projections
        </button>
      </div>

      {/* Setup Tab */}
      {activeTab === 'setup' && <div className="space-y-6" data-id="element-332">
          <div className="flex justify-between items-center" data-id="element-333">
            <h3 className="text-xl font-semibold text-white" data-id="element-334">
              Your Portfolio ({properties.length}/10)
            </h3>
            <button onClick={addProperty} disabled={properties.length >= 10} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium" data-id="element-335">
              <PlusIcon className="w-4 h-4" data-id="element-336" />
              Add Property
            </button>
          </div>

          <div className="space-y-4" data-id="element-337">
            {properties.map((prop, index) => <div key={prop.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-id="element-338">
                {/* Property Header (Collapsible) */}
                <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors" onClick={() => setExpandedPropId(expandedPropId === prop.id ? null : prop.id)} data-id="element-339">
                  <div className="flex items-center gap-4" data-id="element-340">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold" data-id="element-341">
                      {index + 1}
                    </div>
                    <div data-id="element-342">
                      <h4 className="text-white font-medium" data-id="element-343">{prop.name}</h4>
                      <p className="text-sm text-slate-400" data-id="element-344">
                        Value: {formatCurrency(prop.value)} | Loans:{' '}
                        {prop.loans.length}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-345">
                    <button onClick={e => {
                e.stopPropagation();
                removeProperty(prop.id);
              }} className="p-2 text-slate-500 hover:text-red-400 transition-colors" title="Remove Property" data-id="element-346">
                      <Trash2Icon className="w-5 h-5" data-id="element-347" />
                    </button>
                    {expandedPropId === prop.id ? <ChevronUpIcon className="w-5 h-5 text-slate-400" data-id="element-348" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-349" />}
                  </div>
                </div>

                {/* Property Details (Expanded) */}
                {expandedPropId === prop.id && <div className="p-6 border-t border-white/10 bg-[#0a0a1a]/50" data-id="element-350">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-id="element-351">
                      <div className="lg:col-span-2" data-id="element-352">
                        <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-353">
                          Property Name
                        </label>
                        <input type="text" value={prop.name} onChange={e => updateProperty(prop.id, 'name', e.target.value)} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-354" />
                      </div>
                      <div data-id="element-355">
                        <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-356">
                          Current Value ($)
                        </label>
                        <input type="number" value={prop.value} onChange={e => updateProperty(prop.id, 'value', Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-357" />
                      </div>
                      <div data-id="element-358">
                        <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-359">
                          Weekly Rent ($)
                        </label>
                        <input type="number" value={prop.weeklyRent} onChange={e => updateProperty(prop.id, 'weeklyRent', Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-360" />
                      </div>
                      <div data-id="element-361">
                        <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-362">
                          Annual Expenses ($)
                        </label>
                        <input type="number" value={prop.annualExpenses} onChange={e => updateProperty(prop.id, 'annualExpenses', Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-363" />
                      </div>
                    </div>

                    {/* Loans Section */}
                    <div data-id="element-364">
                      <div className="flex justify-between items-center mb-4" data-id="element-365">
                        <h5 className="text-sm font-semibold text-slate-300" data-id="element-366">
                          Associated Loans / Splits
                        </h5>
                        <button onClick={() => addLoan(prop.id)} disabled={prop.loans.length >= 10} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded-md transition-colors text-xs font-medium" data-id="element-367">
                          <PlusIcon className="w-3 h-3" data-id="element-368" />
                          Add Split
                        </button>
                      </div>

                      {prop.loans.length === 0 ? <p className="text-sm text-slate-500 italic" data-id="element-369">
                          No loans attached to this property.
                        </p> : <div className="space-y-3" data-id="element-370">
                          {prop.loans.map(loan => <div key={loan.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-white/5 p-3 rounded-xl border border-white/5" data-id="element-371">
                              <div className="md:col-span-3" data-id="element-372">
                                <label className="block text-xs text-slate-400 mb-1" data-id="element-373">
                                  Loan Name
                                </label>
                                <input type="text" value={loan.name} onChange={e => updateLoan(prop.id, loan.id, 'name', e.target.value)} className="w-full bg-[#0a0a1a] border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500" data-id="element-374" />
                              </div>
                              <div className="md:col-span-2" data-id="element-375">
                                <label className="block text-xs text-slate-400 mb-1" data-id="element-376">
                                  Amount ($)
                                </label>
                                <input type="number" value={loan.amount} onChange={e => updateLoan(prop.id, loan.id, 'amount', Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500" data-id="element-377" />
                              </div>
                              <div className="md:col-span-1" data-id="element-378">
                                <label className="block text-xs text-slate-400 mb-1" data-id="element-379">
                                  Rate (%)
                                </label>
                                <input type="number" step="0.1" value={loan.rate} onChange={e => updateLoan(prop.id, loan.id, 'rate', Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500" data-id="element-380" />
                              </div>
                              <div className="md:col-span-1" data-id="element-381">
                                <label className="block text-xs text-slate-400 mb-1" data-id="element-382">
                                  Type
                                </label>
                                <select value={loan.type} onChange={e => updateLoan(prop.id, loan.id, 'type', e.target.value as LoanType)} className="w-full bg-[#0a0a1a] border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 appearance-none" data-id="element-383">
                                  <option value="PI" data-id="element-384">P&I</option>
                                  <option value="IO" data-id="element-385">IO</option>
                                </select>
                              </div>
                              <div className="md:col-span-1" data-id="element-386">
                                <label className="block text-xs text-slate-400 mb-1" data-id="element-387">
                                  Term (Yr)
                                </label>
                                <input type="number" value={loan.term} onChange={e => updateLoan(prop.id, loan.id, 'term', Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500" data-id="element-388" />
                              </div>
                              <div className="md:col-span-3" data-id="element-389">
                                <label className="block text-xs text-slate-400 mb-1" data-id="element-390">
                                  Secured Against (Equity Release)
                                </label>
                                <select value={loan.securedAgainstId} onChange={e => updateLoan(prop.id, loan.id, 'securedAgainstId', e.target.value)} className="w-full bg-[#0a0a1a] border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 appearance-none" data-id="element-391">
                                  {properties.map(p => <option key={p.id} value={p.id} data-id="element-392">
                                      {p.name}
                                    </option>)}
                                </select>
                              </div>
                              <div className="md:col-span-1 flex justify-end pb-1" data-id="element-393">
                                <button onClick={() => removeLoan(prop.id, loan.id)} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-400/10 rounded-md transition-colors" title="Remove Loan" data-id="element-394">
                                  <Trash2Icon className="w-4 h-4" data-id="element-395" />
                                </button>
                              </div>
                            </div>)}
                        </div>}
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>}

      {/* Projections Tab */}
      {activeTab === 'projections' && <div className="space-y-8" data-id="element-396">
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10 h-[500px]" data-id="element-397">
            <h4 className="text-lg font-semibold text-white mb-6" data-id="element-398">
              10-Year Portfolio Growth & Cashflow
            </h4>
            <ResponsiveContainer width="100%" height="100%" data-id="element-399">
              <ComposedChart data={results.projections} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0
          }} data-id="element-400">
                <defs data-id="element-401">
                  <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1" data-id="element-402">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-403" />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-404" />
                  </linearGradient>
                  <linearGradient id="colorDebt" x1="0" y1="0" x2="0" y2="1" data-id="element-405">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} data-id="element-406" />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} data-id="element-407" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-408" />
                <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `Year ${val}`} data-id="element-409" />
                <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `$${(val / 1000000).toFixed(1)}M`} data-id="element-410" />
                <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `$${(val / 1000).toFixed(0)}k`} data-id="element-411" />
                <Tooltip contentStyle={{
              backgroundColor: '#0f172a',
              borderColor: '#1e293b',
              borderRadius: '8px',
              color: '#fff'
            }} formatter={(value: number, name: string) => [formatCurrency(value), name]} labelFormatter={label => `Year ${label}`} data-id="element-412" />
                <Legend wrapperStyle={{
              paddingTop: '20px'
            }} data-id="element-413" />
                <Area yAxisId="left" type="monotone" dataKey="equity" name="Total Equity" stroke="#10b981" strokeWidth={2} fill="url(#colorEquity)" stackId="1" data-id="element-414" />
                <Area yAxisId="left" type="monotone" dataKey="debt" name="Total Debt" stroke="#ef4444" strokeWidth={2} fill="url(#colorDebt)" stackId="1" data-id="element-415" />
                <Line yAxisId="left" type="monotone" dataKey="value" name="Portfolio Value" stroke="#f8fafc" strokeWidth={2} dot={false} data-id="element-416" />
                <Bar yAxisId="right" dataKey="netCashflow" name="Net Cashflow" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={20} data-id="element-417" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden" data-id="element-418">
            <div className="overflow-x-auto" data-id="element-419">
              <table className="w-full text-left text-sm text-slate-300" data-id="element-420">
                <thead className="bg-white/5 text-slate-400 font-medium border-b border-white/10" data-id="element-421">
                  <tr data-id="element-422">
                    <th className="px-6 py-4" data-id="element-423">Year</th>
                    <th className="px-6 py-4" data-id="element-424">Portfolio Value</th>
                    <th className="px-6 py-4" data-id="element-425">Total Debt</th>
                    <th className="px-6 py-4" data-id="element-426">Total Equity</th>
                    <th className="px-6 py-4" data-id="element-427">Annual Rent</th>
                    <th className="px-6 py-4" data-id="element-428">
                      Annual Costs (Exp + Repayments)
                    </th>
                    <th className="px-6 py-4" data-id="element-429">Net Cashflow</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5" data-id="element-430">
                  {results.projections.map(proj => <tr key={proj.year} className="hover:bg-white/[0.02] transition-colors" data-id="element-431">
                      <td className="px-6 py-4 font-medium text-white" data-id="element-432">
                        Year {proj.year}
                      </td>
                      <td className="px-6 py-4" data-id="element-433">
                        {formatCurrency(proj.value)}
                      </td>
                      <td className="px-6 py-4 text-red-400" data-id="element-434">
                        {formatCurrency(proj.debt)}
                      </td>
                      <td className="px-6 py-4 text-emerald-400" data-id="element-435">
                        {formatCurrency(proj.equity)}
                      </td>
                      <td className="px-6 py-4 text-blue-400" data-id="element-436">
                        {formatCurrency(proj.rent)}
                      </td>
                      <td className="px-6 py-4 text-orange-400" data-id="element-437">
                        {formatCurrency(proj.expenses + proj.repayments)}
                      </td>
                      <td className={`px-6 py-4 font-bold ${proj.netCashflow >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-438">
                        {formatCurrency(proj.netCashflow)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}